import React from "react";

import styled, { css } from "styled-components";

import { color } from "../../tokens";

const fontSizes = {
  display: "2rem",
  titleLarge: "1.5rem",
  titleMedium: "1.25rem",
  titleSmall: "1rem",
  subTitle: "1rem",
  bodyLarge: "1rem",
  bodyMedium: "0.875rem",
  bodySmall: "0.75rem",
  fieldTitle: "0.75rem",
};

const fontWeights = {
  display: 700,
  titleLarge: 600,
  titleMedium: 600,
  titleSmall: 600,
  subTitle: 600,
  bodyLarge: 400,
  bodyMedium: 400,
  bodySmall: 400,
  fieldTitle: 500,
};

const lineHeights = {
  display: "2.5rem",
  titleLarge: "1.75rem",
  titleMedium: "1.75rem",
  titleSmall: "1.5rem",
  subTitle: "1.5rem",
  bodyLarge: "1.5rem",
  bodyMedium: "1.5rem",
  bodySmall: "1.125rem",
  fieldTitle: "1.125rem",
};

const letterSpacings = {
  display: "-0.025rem",
  titleLarge: "-0.025rem",
  titleMedium: "-0.025rem",
  titleSmall: "-0.025rem",
  subTitle: "-0.025rem",
  bodyLarge: "-0.013rem",
  bodyMedium: "-0.013rem",
  bodySmall: "-0.013rem",
  fieldTitle: "-0.013rem",
};

interface Props {
  variant:
    | "display"
    | "titleLarge"
    | "titleMedium"
    | "titleSmall"
    | "subTitle"
    | "bodyLarge"
    | "bodyMedium"
    | "bodySmall"
    | "fieldTitle";
  noSelect?: boolean;
  children?: React.ReactNode;
  maxLines?: number; // Optional: max number of lines before truncating with ellipsis
  [key: string]: any; // Allow additional props for styling or other purposes
}

// Create styled components outside the render function
const createStyledTag = (tag: keyof JSX.IntrinsicElements) => styled(
  tag
)<Props>`
  margin: 0;
  font-family: "InterVariable", -apple-system, BlinkMacSystemFont, "Segoe UI",
    "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans",
    "Helvetica Neue", sans-serif;
  color: ${(props) => (props.color ? `${props.color}` : color.neutral[500])};
  font-size: ${(props) => fontSizes[props.variant]};
  line-height: ${(props) => lineHeights[props.variant]};
  font-weight: ${(props) => fontWeights[props.variant]};
  letter-spacing: ${(props) => letterSpacings[props.variant]};
  text-transform: ${(props) =>
    props.variant === "fieldTitle" ? "uppercase" : "none"};
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
  user-select: ${(props) => (props.noSelect ? "none" : "text")};
  ${(props) =>
    props.maxLines &&
    props.maxLines > 0 &&
    css`
      display: -webkit-box;
      -webkit-line-clamp: ${props.maxLines};
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    `}
`;

const Display = createStyledTag("div");
const TitleLarge = createStyledTag("h1");
const TitleMedium = createStyledTag("h2");
const TitleSmall = createStyledTag("h3");
const SubTitle = createStyledTag("h4");
const BodyLarge = createStyledTag("p");
const BodyMedium = createStyledTag("p");
const BodySmall = createStyledTag("p");
const FieldTitle = createStyledTag("p");

const styledComponentMap: Record<
  Props["variant"],
  React.ComponentType<Props>
> = {
  display: Display,
  titleLarge: TitleLarge,
  titleMedium: TitleMedium,
  titleSmall: TitleSmall,
  subTitle: SubTitle,
  bodyLarge: BodyLarge,
  bodyMedium: BodyMedium,
  bodySmall: BodySmall,
  fieldTitle: FieldTitle,
};

export const Typography: React.FC<Props> = (props) => {
  const { variant, children, ...rest } = props;

  const Tag = styledComponentMap[variant];

  return (
    <Tag {...rest} variant={variant}>
      {children}
    </Tag>
  );
};

export type Variants = Props["variant"];
